import type { DocMode } from '@blocksuite/blocks';
import { DocTitle, EdgelessEditor, PageEditor } from '@blocksuite/presets';
import type { Doc } from '@blocksuite/store';
import {
  createReactComponentFromLit,
  useConfirmModal,
  useLitPortalFactory,
} from '@pique/component';
import { PeekViewService } from '@pique/core/modules/peek-view';
import {
  ActionItemsService,
  AttendeesService,
  FollowupEmailService,
  TranscriptionService,
} from '@pique/core/modules/properties';
import { WorkbenchService } from '@pique/core/modules/workbench';
import {
  DocService,
  DocsService,
  useFramework,
  useLiveData,
  useService,
} from '@toeverything/infra';
import React, {
  Fragment,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DocsSearchService } from '../../../modules/docs-search';
import { PagePropertiesTable } from '../../pique/page-properties';
import { PiquePageReference } from '../../pique/reference-link';
import LectureScorecard from './lecture-scorecard';
import { videoCompletedSlot } from './specs/custom/pique/pique-slots';
import {
  patchActionItemsService,
  patchAttendeesService,
  patchDocModeService,
  patchDocsSearchService,
  patchEdgelessClipboard,
  patchFollowupEmailService,
  patchForSharedPage,
  patchNotificationService,
  patchPeekViewService,
  patchQuickSearchService,
  patchReferenceRenderer,
  patchTranscriptionService,
  type ReferenceReactRenderer,
} from './specs/custom/spec-patchers';
import { createEdgelessModeSpecs } from './specs/edgeless';
import { createPageModeSpecs } from './specs/page';
import * as styles from './styles.css';

const adapted = {
  DocEditor: createReactComponentFromLit({
    react: React,
    elementClass: PageEditor,
  }),
  DocTitle: createReactComponentFromLit({
    react: React,
    elementClass: DocTitle,
  }),
  EdgelessEditor: createReactComponentFromLit({
    react: React,
    elementClass: EdgelessEditor,
  }),
};

interface BlocksuiteEditorProps {
  page: Doc;
  sourcePageId?: string;
  shared?: boolean;
}

const usePatchSpecs = (
  page: Doc,
  shared: boolean,
  mode: DocMode,
  sourcePageId?: string
) => {
  const [reactToLit, portals] = useLitPortalFactory();
  const peekViewService = useService(PeekViewService);
  const docService = useService(DocService);
  const docsService = useService(DocsService);
  const actionItemsService = useService(ActionItemsService);
  const attendeesService = useService(AttendeesService);
  const transcriptionService = useService(TranscriptionService);
  const followupEmailService = useService(FollowupEmailService);
  const framework = useFramework();
  const referenceRenderer: ReferenceReactRenderer = useMemo(() => {
    return function customReference(reference) {
      const pageId = reference.delta.attributes?.reference?.pageId;
      if (!pageId) return <span />;
      return (
        <PiquePageReference docCollection={page.collection} pageId={pageId} />
      );
    };
  }, [page.collection]);

  const specs = useMemo(() => {
    return mode === 'edgeless'
      ? createEdgelessModeSpecs(framework)
      : createPageModeSpecs(framework);
  }, [mode, framework]);

  const confirmModal = useConfirmModal();
  const docsSearchService = framework.get(DocsSearchService);
  const sourcePageBinding = useMemo(
    () => ({
      renderedPageId: page.id,
      sourcePageId,
    }),
    [page.id, sourcePageId]
  );
  const patchedSpecs = useMemo(() => {
    let patched = patchReferenceRenderer(specs, reactToLit, referenceRenderer);
    patched = patchNotificationService(
      patchReferenceRenderer(patched, reactToLit, referenceRenderer),
      confirmModal
    );
    patched = patchPeekViewService(patched, peekViewService);
    patched = patchActionItemsService(
      patched,
      actionItemsService,
      sourcePageBinding
    );
    patched = patchAttendeesService(
      patched,
      attendeesService,
      sourcePageBinding
    );
    patched = patchTranscriptionService(
      patched,
      transcriptionService,
      sourcePageBinding
    );
    patched = patchFollowupEmailService(
      patched,
      followupEmailService,
      sourcePageBinding
    );
    patched = patchEdgelessClipboard(patched);
    if (!page.readonly) {
      patched = patchQuickSearchService(patched, framework);
    }
    if (shared) {
      patched = patchForSharedPage(patched);
    }
    patched = patchDocModeService(patched, docService, docsService);
    patched = patchDocsSearchService(patched, docsSearchService);

    return patched;
  }, [
    actionItemsService,
    attendeesService,
    confirmModal,
    docService,
    docsService,
    framework,
    page.readonly,
    peekViewService,
    reactToLit,
    referenceRenderer,
    shared,
    specs,
    sourcePageBinding,
    transcriptionService,
    followupEmailService,
    docsSearchService,
  ]);

  return [
    patchedSpecs,
    useMemo(
      () => (
        <>
          {portals.map((p: { id: string; portal: React.ReactPortal }) => (
            <Fragment key={p.id}>{p.portal}</Fragment>
          ))}
        </>
      ),
      [portals]
    ),
  ] as const;
};

export const BlocksuiteDocEditor = function BlocksuiteDocEditor({
  ref,
  page,
  sourcePageId,
  shared,
}: BlocksuiteEditorProps & { ref?: Ref<PageEditor> }) {
  const titleRef = useRef<DocTitle>(null);
  const docRef = useRef<PageEditor | null>(null);
  const [docPage, setDocPage] =
    useState<HTMLElementTagNameMap['affine-page-root']>();

  const workbench = useService(WorkbenchService).workbench;
  const activeView = useLiveData(workbench.activeView$);
  const hash = useLiveData(activeView?.location$)?.hash;

  const [isVideoCompleted, setIsVideoCompleted] = useState(false);

  useEffect(() => {
    const handleVideoCompleted = (completed: boolean) => {
      setIsVideoCompleted(completed);
    };

    const disposable = videoCompletedSlot.on(handleVideoCompleted);
    return () => {
      disposable.dispose();
    };
  }, []);

  const onDocRef = useCallback(
    (el: PageEditor) => {
      docRef.current = el;
      if (ref) {
        if (typeof ref === 'function') {
          ref(el);
        } else {
          ref.current = el;
        }
      }
    },
    [ref]
  );

  useEffect(() => {
    if (!docRef.current) {
      return;
    }

    docRef.current.updateComplete
      .then(() => {
        const docPage = docRef.current?.querySelector('affine-page-root');
        if (docPage) {
          setDocPage(docPage);
        }

        // If there's a hash in the URL, don't auto-focus as it might interfere with navigation
        if (hash) {
          return;
        }

        setTimeout(() => {
          const firstParagraph = docPage?.querySelector('rich-text');
          if (
            firstParagraph?.inlineEditor &&
            firstParagraph.inlineEditor.yText.toString().trim().length > 0
          ) {
            return;
          }

          // Try to focus using the page's focusFirstParagraph method first
          if (docPage?.focusFirstParagraph) {
            docPage.focusFirstParagraph();
            return;
          }

          // Fallback: Try to find and focus the first paragraph directly
          if (firstParagraph?.inlineEditor) {
            firstParagraph.inlineEditor.focusEnd();
            return;
          }

          // Last resort: Focus title if nothing else worked
          if (titleRef.current) {
            const richText = titleRef.current.querySelector('rich-text');
            richText?.inlineEditor?.focusEnd();
          }
        }, 300);
      })
      .catch(console.error);
  }, [hash]);

  const [specs, portals] = usePatchSpecs(page, !!shared, 'page', sourcePageId);
  const propertiesPageId = sourcePageId ?? page.id;

  return (
    <>
      <div className={styles.affineDocViewport} style={{ height: '100%' }}>
        <adapted.DocTitle doc={page} ref={titleRef} />
        <PagePropertiesTable docId={propertiesPageId} />
        <adapted.DocEditor
          className={styles.docContainer}
          ref={onDocRef}
          doc={page}
          specs={specs}
          hasViewport={false}
        />
        {docPage && !page.readonly ? (
          <div
            className={styles.docEditorGap}
            onClick={() => {
              docPage.std.command.exec('appendParagraph' as never, {});
            }}
          ></div>
        ) : null}
        {isVideoCompleted ? <LectureScorecard /> : null}
      </div>
      {portals}
    </>
  );
};
export const BlocksuiteEdgelessEditor = function BlocksuiteEdgelessEditor({
  ref,
  page,
  sourcePageId,
  shared,
}: BlocksuiteEditorProps & { ref?: Ref<EdgelessEditor> }) {
  const [specs, portals] = usePatchSpecs(
    page,
    !!shared,
    'edgeless',
    sourcePageId
  );
  const editorRef = useRef<EdgelessEditor | null>(null);

  const onDocRef = useCallback(
    (el: EdgelessEditor) => {
      editorRef.current = el;
      if (ref) {
        if (typeof ref === 'function') {
          ref(el);
        } else {
          ref.current = el;
        }
      }
    },
    [ref]
  );

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.updateComplete
        .then(() => {
          // make sure editor can get keyboard events on showing up
          editorRef.current?.querySelector('affine-edgeless-root')?.click();
        })
        .catch(console.error);
    }
  }, []);

  return (
    <>
      <div
        data-testid="edgeless-mindmap-view"
        style={{ width: '100%', height: '100%' }}
      >
        <adapted.EdgelessEditor ref={onDocRef} doc={page} specs={specs} />
      </div>
      {portals}
    </>
  );
};
